import { Box, Flex, Skeleton, Text } from '@chakra-ui/react';
import { useResources } from 'datx-jsonapi-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { LectureLanguageDropdown } from '@/components/ui/features/lecture/LectureLanguageDropdown/LectureLanguageDropdown';
import { LecturePlayerPanel } from '@/components/ui/features/lecture/LecturePlayer/components/LecturePlayerPanel';
import { useLecturePlayerContext } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.context';
import { usePlayerState } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.hooks';
import { TranscriptText } from '@/components/ui/shared/transcripts/TranscriptText/TranscriptText';
import useEventListener from '@/hooks/useEventListener';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';

export const LecturePlayerTranscript: FC = () => {
  const { t } = useTranslation();
  const { id: lectureId } = useLecturePageQueryParams();
  const [player] = useLecturePlayerContext();
  const { data: transcripts, isValidating } = useResources(queryTranscriptsByLectureId(lectureId));
  const [activeCues, setActiveCues] = useState<Array<VTTCue>>([]);
  const { activeTrack, cues, isLoadingMetadata } = usePlayerState();
  const isOpen = Boolean(activeTrack);
  const isLoading = isLoadingMetadata || isValidating;

  const handleCueClick = (cue: VTTCue) => {
    player.currentTime(cue.startTime);
  };

  const handleCueChange = () => {
    setActiveCues(Array.from(activeTrack.activeCues) as Array<VTTCue>);
  };

  useEventListener('cuechange', handleCueChange, activeTrack);

  if (isLoading) {
    return <Skeleton w={{ base: 'full', md: '389px' }} h={{ base: '291px', md: 'full' }} />;
  }

  if (!transcripts) return null;

  return (
    <LecturePlayerPanel isOpen={isOpen}>
      <Flex align="center">
        <Text flex="1 1 auto">{t('newLecture.transcript')}</Text>
        <LectureLanguageDropdown />
      </Flex>
      <Box flex="1 1 0" minH="0">
        <TranscriptText h="full" cues={cues} activeCues={activeCues} onCueClick={handleCueClick} />
      </Box>
    </LecturePlayerPanel>
  );
};
